import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  signInWithRedirect,
  signOut as amplifySignOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { configureAmplifyAuth } from "./amplifyConfig";
import { hasOAuthCallbackParams } from "./authRedirect";
import type { AuthContextValue, AuthDisplayError } from "./authTypes";
import { mapAmplifyError } from "./mapAmplifyError";
import { refreshSessionState } from "./refreshSessionState";

export type { AuthContextValue, AuthDisplayError } from "./authTypes";

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_EXPIRED_ERROR: AuthDisplayError = {
  source: "i18n",
  key: "auth.reason.expired",
};

function rawError(error: unknown): AuthDisplayError {
  return { source: "raw", message: mapAmplifyError(error) };
}

function isOAuthReturn(): boolean {
  if (typeof window === "undefined") return false;
  return hasOAuthCallbackParams(new URLSearchParams(window.location.search));
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<AuthDisplayError | null>(null);

  useEffect(() => {
    configureAmplifyAuth();
    let cancelled = false;

    void (async () => {
      const signedIn = await refreshSessionState();
      if (cancelled) return;
      if (signedIn) {
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }
      // Returning from Cognito/Google: Amplify’s OAuth listener still exchanging the code.
      // Stay on loading chrome until Hub reports success or failure.
      if (!isOAuthReturn()) {
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    })();

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "signInWithRedirect":
          setIsAuthenticated(true);
          setError(null);
          setIsLoading(false);
          break;
        case "signedOut":
          setIsAuthenticated(false);
          setIsLoading(false);
          break;
        case "signInWithRedirect_failure":
          setIsAuthenticated(false);
          setError(rawError(payload.data));
          setIsLoading(false);
          break;
        case "tokenRefresh_failure":
          setIsAuthenticated(false);
          setError(SESSION_EXPIRED_ERROR);
          setIsLoading(false);
          break;
        default:
          break;
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (caught) {
      setError(rawError(caught));
      throw caught;
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      await amplifySignOut();
    } finally {
      setIsAuthenticated(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      error,
      clearError,
      signInWithGoogle,
      signOut,
    }),
    [isLoading, isAuthenticated, error, clearError, signInWithGoogle, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
